import { api } from './client'

/** `POST /api/workspaces/{id}/github-app/flows` — starts the GitHub App
 * manifest flow: a pending connection plus the manifest to POST to
 * `github_url` (GitHub's new-App page, `state` included). */
export interface GithubAppFlowResponse {
  connection_id: string
  github_url: string
  manifest: unknown
}

/** `POST /api/github-app/exchange` — completes the flow after GitHub
 * redirects back with `code` and `state`. */
export interface GithubAppExchangeResponse {
  connection_id: string
  /** Where the user installs the new App. */
  install_url: string
}

/** `POST /api/workspaces/{id}/github-app/install-flows` — starts the
 * central-App install flow: a pending connection plus GitHub's install
 * page (carrying `state`, which GitHub echoes on the redirect when it asks
 * for user authorization). The SPA also holds the state in session storage
 * as a fallback. */
export interface GithubInstallFlowResponse {
  connection_id: string
  install_url: string
  state: string
}

/** `POST /api/github-app/install-claim` — binds the installation the user
 * just created to the pending connection. GitHub's user-authorization
 * `code` proves the installer can access the installation. */
export interface GithubInstallClaimResponse {
  connection_id: string
  workspace_id: string
  account_login: string
}

/** sessionStorage record for an in-flight install flow; written when the
 * flow starts, read (and cleared) by the installed page. */
interface HeldInstallFlow {
  state: string
  workspaceId: string
}

const INSTALL_FLOW_KEY = 'cf-github-install-flow'

export function holdInstallFlow(flow: HeldInstallFlow) {
  sessionStorage.setItem(INSTALL_FLOW_KEY, JSON.stringify(flow))
}

export function takeHeldInstallFlow(): HeldInstallFlow | null {
  const raw = sessionStorage.getItem(INSTALL_FLOW_KEY)
  if (!raw) return null
  sessionStorage.removeItem(INSTALL_FLOW_KEY)
  try {
    return JSON.parse(raw) as HeldInstallFlow
  } catch {
    return null
  }
}

/** sessionStorage key for an own-App (manifest) setup's connection id: the
 * exchange knows the connection, but GitHub's post-install redirect (the
 * App's `setup_url`) lands on the workspace resources page without it. */
const PENDING_SETUP_KEY = 'cf-github-pending-setup'

export function holdPendingAppSetup(connectionId: string) {
  sessionStorage.setItem(PENDING_SETUP_KEY, connectionId)
}

/** The held connection id, cleared once read. */
export function takePendingAppSetup(): string | null {
  const id = sessionStorage.getItem(PENDING_SETUP_KEY)
  sessionStorage.removeItem(PENDING_SETUP_KEY)
  return id
}

export const githubAppApi = {
  startFlow: (workspaceId: string, organization?: string, webUrl?: string) =>
    api.post<GithubAppFlowResponse>(
      `/api/workspaces/${workspaceId}/github-app/flows`,
      {
        ...(organization ? { organization } : {}),
        ...(webUrl ? { web_url: webUrl } : {}),
      },
    ),
  exchange: (code: string, state: string) =>
    api.post<GithubAppExchangeResponse>('/api/github-app/exchange', {
      code,
      state,
    }),
  /** `reconnect` re-binds a disconnected central connection (its routes
   * survive) instead of creating a new one. */
  startInstallFlow: (workspaceId: string, reconnect?: string) =>
    api.post<GithubInstallFlowResponse>(
      `/api/workspaces/${workspaceId}/github-app/install-flows`,
      reconnect ? { reconnect } : undefined,
    ),
  claimInstall: (installationId: string, state: string, code: string) =>
    api.post<GithubInstallClaimResponse>('/api/github-app/install-claim', {
      installation_id: installationId,
      state,
      code,
    }),
}

/** Navigate to GitHub's new-App page by POSTing the manifest as a form
 * field — GitHub's manifest flow takes it only as a form POST. */
export function postManifestToGitHub(flow: GithubAppFlowResponse) {
  const form = document.createElement('form')
  form.method = 'post'
  form.action = flow.github_url
  const input = document.createElement('input')
  input.type = 'hidden'
  input.name = 'manifest'
  input.value = JSON.stringify(flow.manifest)
  form.appendChild(input)
  document.body.appendChild(form)
  form.submit()
}
